import React, { useState } from 'react';
import _ from "lodash";
import { Alert, Tab, Tabs } from "@mui/material";
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import EditCompany, { OPERATIONS_SECTION, ADMIN_SECTION } from './EditCompany';
import CompanySelector from './CompanySelector';
import PluginsIndex from 'app/pages/plugins/PluginsIndex';
import SubscriptionsIndex from 'app/pages/subscriptions/SubscriptionsIndex';
import PrintersIndex from 'app/pages/printers/PrintersIndex';
import RoutingSettingsPanel from 'app/pages/trips/RoutingSettingsPanel';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import IntegrationKeysPanel from 'app/pages/integration_keys/IntegrationKeysPanel';

const INTEGRATIONS_TAB = 'integrations';
const SUBSCRIPTIONS_TAB = 'subscriptions';
const PRINTERS_TAB = 'printers';
const ROUTING_TAB = 'routing';
const API_TAB = 'api';

const CompanySettings = () => {
    const { authUser } = useJumboAuth();
    const currentCompanyId = authUser?.current_company?.id;

    // Los permisos del backend salen de user.current_profiles (ver Ability), o sea
    // los perfiles de la empresa activa. Acá hay que mirar los mismos: si no, quien
    // es customer_admin en una empresa y supervisor en otra ve el panel completo
    // después de rotar.
    //
    // superadmin va sin filtrar, igual que is_global_superadmin en el backend.
    const isSuperadmin = _.some(authUser?.profiles, { name: 'superadmin' });
    const currentCompanyProfileNames = _.chain(authUser?.profiles || [])
        .filter((profile) => `${profile.company_id}` === `${currentCompanyId}`)
        .map('name')
        .uniq()
        .value();

    // Administración (empresa, facturación, notificaciones) e Integraciones son solo
    // para el perfil Administrador. El resto ve únicamente Operaciones, aunque llegue
    // por URL: el sidebar también le muestra Configuración al supervisor, y a
    // cualquier otro perfil le basta con escribir la dirección.
    //
    // Y esto no lo tapa el backend: update_settings permite los mismos campos para
    // supervisor que para customer_admin (Ability :198), así que este cálculo es lo
    // único que deja la facturación y el SMTP fuera de su alcance. No da 403: guarda.
    const isAdmin = isSuperadmin || _.includes(currentCompanyProfileNames, 'customer_admin');

    // Operaciones (planificación, app móvil, retail) queda para Administrador,
    // Supervisor y superadmin. Cualquier otro perfil que llegue por URL no ve el
    // formulario: el sidebar ya no le muestra Configuración, pero la ruta sigue abierta.
    const canViewOperations = isAdmin || _.includes(currentCompanyProfileNames, 'supervisor');

    const [activeTab, setActiveTab] = useState(OPERATIONS_SECTION);
    const [selectedCompanyId, setSelectedCompanyId] = useState(currentCompanyId);

    // Integraciones y Suscripciones van contra current_company sin recibir compania
    // por parametro (ver PluginsController y SubscriptionsController), asi que ahi no
    // existe "mirar otra sin cambiarse": el tab muestra siempre la empresa activa y el
    // selector pasa a modo activar, tambien para el superadmin.
    const followsActiveCompany = activeTab === INTEGRATIONS_TAB || activeTab === SUBSCRIPTIONS_TAB || activeTab === PRINTERS_TAB || activeTab === ROUTING_TAB || activeTab === API_TAB;
    const showsCompanyForm = !followsActiveCompany;
    const companyId = (isSuperadmin && showsCompanyForm) ? selectedCompanyId : currentCompanyId;

    const tabs = [
        { value: OPERATIONS_SECTION, label: 'Operaciones', icon: <TuneOutlinedIcon sx={{ fontSize: 20 }} /> },
        isAdmin && { value: ADMIN_SECTION, label: 'Administración', icon: <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 20 }} /> },
        isAdmin && { value: INTEGRATIONS_TAB, label: 'Integraciones', icon: <AppsOutlinedIcon sx={{ fontSize: 20 }} /> },
        // API: llaves para que el ERP del cliente mande pedidos. Solo Administrador, igual que
        // IntegrationKeysController: una llave mete pedidos a nombre de toda la empresa.
        isAdmin && { value: API_TAB, label: 'API', icon: <ApiOutlinedIcon sx={{ fontSize: 20 }} /> },
        // Impresoras salió del sidebar y vive acá. Lo ve el mismo grupo que lo veía
        // en el menú: Administrador, Supervisor y superadmin.
        { value: PRINTERS_TAB, label: 'Impresoras', icon: <PrintOutlinedIcon sx={{ fontSize: 20 }} /> },
        // Ruteo: como optimiza esta empresa. Lo ve el mismo grupo que Operaciones,
        // porque decidir si el camion vuelve a la bodega o cuanto dura una parada es
        // politica de la operacion, no configuracion tecnica.
        { value: ROUTING_TAB, label: 'Ruteo', icon: <AltRouteOutlinedIcon sx={{ fontSize: 20 }} /> },
        // Suscripciones era superadmin-only en el sidebar; se mantiene igual.
        isSuperadmin && { value: SUBSCRIPTIONS_TAB, label: 'Suscripciones', icon: <SubscriptionsOutlinedIcon sx={{ fontSize: 20 }} /> },
    ].filter(Boolean);

    return (
        <div>
            {authUser && (
                <CompanySelector
                    selectedCompanyId={companyId}
                    onCompanySelect={setSelectedCompanyId}
                    showAllCompanies={isSuperadmin}
                    mode={isSuperadmin && showsCompanyForm ? 'select' : 'activate'}
                />
            )}

            {canViewOperations && (
                <Tabs
                    value={activeTab}
                    onChange={(event, value) => setActiveTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            label={tab.label}
                            icon={tab.icon}
                            iconPosition="start"
                            sx={{ minHeight: 48 }} />
                    ))}
                </Tabs>
            )}

            {/* Integraciones trae su propio guardar; el resto comparte el de EditCompany.
                Al mantener el mismo elemento en la misma posición, cambiar entre
                Operaciones y Administración no remonta el formulario ni pierde lo
                que haya sin guardar. */}
            {authUser && companyId && showsCompanyForm && canViewOperations && (
                <EditCompany
                    company_id={companyId}
                    restrictedView={!isAdmin}
                    section={activeTab} />
            )}

            {authUser && !canViewOperations && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No tienes acceso a esta configuración. Contacta al administrador de tu empresa.
                </Alert>
            )}

            {isAdmin && activeTab === INTEGRATIONS_TAB && <PluginsIndex />}

            {isAdmin && activeTab === API_TAB && <IntegrationKeysPanel />}

            {canViewOperations && activeTab === PRINTERS_TAB && <PrintersIndex embedded />}

            {canViewOperations && activeTab === ROUTING_TAB && <RoutingSettingsPanel companyId={currentCompanyId} />}

            {isSuperadmin && activeTab === SUBSCRIPTIONS_TAB && <SubscriptionsIndex embedded />}
        </div>
    );
};

export default CompanySettings;
